import {memo, useCallback, useMemo} from 'react';

import {Menubar} from 'primereact/menubar';

import {Link} from 'react-router-dom';
import LogoWithoutBrand from '../assets/logo/logo_without_brand.png';
import {useAppSelector, useTranslation} from '../hooks';
import {languageState} from '../redux/slices/app';
import type {MenuItem} from '../types';

const AppMenu = () => {
  const {t} = useTranslation();
  const selectedLanguage = useAppSelector(languageState);

  const itemRenderer = useCallback((item: MenuItem) => {
    return (
      <Link
        to={item.url as string}
        className="flex align-items-center p-menuitem-link"
        role="menuitem"
        aria-label={item.label}>
        <span className={item.icon} />
        <span className="mx-2">{item.label}</span>
        {item.shortcut && (
          <span className="ml-auto text-xs p-1">{item.shortcut}</span>
        )}
      </Link>
    );
  }, []);

  const start = useMemo(
    () => (
      <Link to={'/' + selectedLanguage + t('pathMenuHome')}>
        <img
          alt="logo"
          src={LogoWithoutBrand}
          height="40"
          className="mr-2"></img>
      </Link>
    ),
    [selectedLanguage, t],
  );

  const menuItems = useMemo(
    () => [
      {
        id: 'menuHome',
        label: t('menuHome'),
        icon: 'pi pi-home',
        url: `/${selectedLanguage}` + t('pathMenuHome'),
      },
      {
        id: 'menuAbout',
        label: t('menuAbout'),
        icon: 'pi pi-briefcase',
        url: `/${selectedLanguage}` + t('pathMenuAbout'),
      },
      {
        id: 'menuProductsTitle',
        label: t('menuProductsTitle'),
        icon: 'pi pi-th-large',
        role: 'menuitem',
        'aria-haspopup': 'true',
        'aria-expanded': 'false',
        items: [
          {
            id: 'menuProductsSubTitle1',
            label: t('menuProductsSubTitle1'),
            shortcut: '❯',
            template: itemRenderer,
            url: `/${selectedLanguage}` + t('pathMenuProductsSubTitle1'),
            items: [
              {
                id: 'menuProductsSubTitle1Option1',
                label: t('menuProductsSubTitle1Option1'),
                template: itemRenderer,
                url:
                  `/${selectedLanguage}` +
                  t('pathMenuProductsSubTitle1Option1'),
              },
              {
                id: 'menuProductsSubTitle1Option4',
                label: t('menuProductsSubTitle1Option4'),
                template: itemRenderer,
                url:
                  `/${selectedLanguage}` +
                  t('pathMenuProductsSubTitle1Option4'),
              },
              {
                id: 'menuProductsSubTitle1Option6',
                label: t('menuProductsSubTitle1Option6'),
                template: itemRenderer,
                url:
                  `/${selectedLanguage}` +
                  t('pathMenuProductsSubTitle1Option6'),
              },
              {
                id: 'menuProductsSubTitle1Option7',
                label: t('menuProductsSubTitle1Option7'),
                template: itemRenderer,
                url:
                  `/${selectedLanguage}` +
                  t('pathMenuProductsSubTitle1Option7'),
              },
              {
                id: 'menuProductsSubTitle1Option8',
                label: t('menuProductsSubTitle1Option8'),
                template: itemRenderer,
                url:
                  `/${selectedLanguage}` +
                  t('pathMenuProductsSubTitle1Option8'),
              },
              {
                id: 'menuProductsSubTitle1Option9',
                label: t('menuProductsSubTitle1Option9'),
                template: itemRenderer,
                url:
                  `/${selectedLanguage}` +
                  t('pathMenuProductsSubTitle1Option9'),
              },
            ],
          },
          {
            id: 'menuProductsSubTitle2',
            label: t('menuProductsSubTitle2'),
            shortcut: '❯',
            template: itemRenderer,
            url: `/${selectedLanguage}` + t('pathMenuProductsSubTitle2'),
            items: [
              {
                id: 'menuProductsSubTitle2Option1',
                label: t('menuProductsSubTitle2Option1'),
                template: itemRenderer,
                url:
                  `/${selectedLanguage}` +
                  t('pathMenuProductsSubTitle2Option1'),
              },
              {
                id: 'menuProductsSubTitle2Option5',
                label: t('menuProductsSubTitle2Option5'),
                template: itemRenderer,
                url:
                  `/${selectedLanguage}` +
                  t('pathMenuProductsSubTitle2Option5'),
              },
            ],
          },
          {
            id: 'menuProductsSubTitle3',
            label: t('menuProductsSubTitle3'),
            shortcut: '❯',
            template: itemRenderer,
            url: `/${selectedLanguage}` + t('pathMenuProductsSubTitle3'),
            items: [
              {
                id: 'menuProductsSubTitle3Option1',
                label: t('menuProductsSubTitle3Option1'),
                template: itemRenderer,
                url:
                  `/${selectedLanguage}` +
                  t('pathMenuProductsSubTitle3Option1'),
              },
              {
                id: 'menuProductsSubTitle3Option3',
                label: t('menuProductsSubTitle3Option3'),
                template: itemRenderer,
                url:
                  `/${selectedLanguage}` +
                  t('pathMenuProductsSubTitle3Option3'),
              },
              {
                id: 'menuProductsSubTitle3Option4',
                label: t('menuProductsSubTitle3Option4'),
                template: itemRenderer,
                url:
                  `/${selectedLanguage}` +
                  t('pathMenuProductsSubTitle3Option4'),
              },
            ],
          },
          {
            id: 'menuProductsSubTitle4',
            label: t('menuProductsSubTitle4'),
            shortcut: '❯',
            template: itemRenderer,
            url: `/${selectedLanguage}` + t('pathMenuProductsSubTitle4'),
            items: [
              {
                id: 'menuProductsSubTitle4Option1',
                label: t('menuProductsSubTitle4Option1'),
                template: itemRenderer,
                url:
                  `/${selectedLanguage}` +
                  t('pathMenuProductsSubTitle4Option1'),
              },
            ],
          },
        ],
      },
      {
        id: 'menuContact',
        label: t('menuContact'),
        icon: 'pi pi-envelope',
        template: itemRenderer,
        url: `/${selectedLanguage}` + t('pathMenuContact'),
      },
    ],
    [itemRenderer, selectedLanguage, t],
  );

  return <Menubar model={menuItems} start={start} aria-label="Main Menu" />;
};

export default memo(AppMenu);
